import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function AccountSettings() {
  const { user, refresh } = useAuth();
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingPassword, setLoadingPassword] = useState(false);
  
  // Email state
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [loadingEmail, setLoadingEmail] = useState(false);

  const validatePassword = (pass) => {
    if (pass.length < 8) return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(pass)) return "Password must contain an uppercase letter.";
    if (!/[a-z]/.test(pass)) return "Password must contain a lowercase letter.";
    if (!/[0-9]/.test(pass)) return "Password must contain a digit.";
    if (!/[^A-Za-z0-9]/.test(pass)) return "Password must contain a special character.";
    return null;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    const passError = validatePassword(newPassword);
    if (passError) {
      toast.error(passError);
      return;
    }

    setLoadingPassword(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (data.access_token) {
        localStorage.setItem("gc_token", data.access_token);
      }
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setLoadingEmail(true);
    try {
      await api.post("/auth/request-email-change", {
        new_email: newEmail,
        current_password: emailPassword,
      });
      toast.success("Verification email sent to the new address");
      setNewEmail("");
      setEmailPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request email change");
    } finally {
      setLoadingEmail(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Account & Security</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage your account credentials and security settings.</p>
      </div>

      <div className="space-y-6">
        {/* Change Password Section */}
        <section>
          <div className="overline text-zinc-500 mb-4">Change Password</div>
          <Card className="p-6">
            <form onSubmit={handlePasswordChange} className="space-y-4" data-testid="change-password-form">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                disabled={loadingPassword}
                data-testid="change-password-submit"
              >
                {loadingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update Password
              </Button>
            </form>
          </Card>
        </section>

        {/* Change Email Section */}
        {user?.role === "admin" && (
          <section>
            <div className="overline text-zinc-500 mb-4">Change Email Address</div>
            <Card className="p-6 space-y-4">
              <div>
                <Label className="text-zinc-500">Current Email</Label>
                <div className="font-medium mt-1">{user?.email}</div>
              </div>
              <form onSubmit={handleEmailChange} className="space-y-4" data-testid="change-email-form">
                <div className="space-y-2">
                  <Label htmlFor="newEmail">New Email Address</Label>
                  <Input
                    id="newEmail"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    data-testid="new-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailPassword">Current Password</Label>
                  <Input
                    id="emailPassword"
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    required
                    data-testid="email-password-input"
                  />
                  <p className="text-xs text-zinc-500">Required to authorize email change.</p>
                </div>
                <Button
                  type="submit"
                  className="bg-zinc-900 text-white hover:bg-zinc-800"
                  disabled={loadingEmail}
                  data-testid="change-email-submit"
                >
                  {loadingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Request Email Change
                </Button>
              </form>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
}
